import { Trans, useLingui } from "@lingui/react/macro";
import { isTomorrow } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGroupStatusTimestamp,
  groupStatusSchema,
  timeOfDayDictionary,
  timeOfDaySchema,
  type IGroupOfActivityOccurrence,
} from "@medoma/models/activities";
import { Text } from "@medoma/components/Text/Text";
import { OutlinedButton } from "@medoma/components/Button/Button";
import styles from "./ActivityItem.module.scss";
import { ActivityTag } from "@/components/ActivityTag/ActivityTag";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { Time } from "@/components/Time/Time";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { getPatientNameWithStatus } from "@/api/Patients";
import { finishOccurrenceGroup } from "@/api/Activities";
import { deducedError, displayErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useSelectedDateIsToday } from "@/Utils/useSelectedDate";

export const GroupItem = ({ group }: { group: IGroupOfActivityOccurrence }) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const allowFinishGroup = useSelectedDateIsToday();
  const { mutate: finishGroup, isPending: isFinishingGroup } = useMutation({
    mutationFn: () =>
      finishOccurrenceGroup({
        id: group.id,
        occurrenceIds: group.occurrences.map((occurrence) => occurrence.id),
      }),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att markera besöket som utfört.`} ${deducedError(error)}`,
      );
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const anyTime = t(timeOfDayDictionary.Any.short);
  const heading = (
    <ActivityTag
      category={group.category}
      title={getPatientNameWithStatus(group.patient, "full")}
      patientId={group.patient.id}
    />
  );
  const finishedAt = getGroupStatusTimestamp(group);

  if (group.visitStatus === groupStatusSchema.enum.finished) {
    return (
      <article className={styles.activityItem}>
        <div className={styles.headingAndTime}>
          {heading}
          <StatusTag
            status="finished"
            timestamp={finishedAt}
            variant={
              finishedAt ? "icon-and-text-with-timestamp" : "icon-and-text"
            }
          />
        </div>
      </article>
    );
  }

  return (
    <article className={styles.activityItem}>
      <div className={styles.headingAndTime}>
        {heading}
        <Time>
          {isTomorrow(group.start) &&
          group.timeOfDay === timeOfDaySchema.enum.Any
            ? t`Imorgon ${anyTime}`
            : group.timeOfDay === timeOfDaySchema.enum.Any
              ? anyTime
              : formattedTimeSpan(group.start, group.end)}
        </Time>
      </div>
      <div className={styles.titleAndDescription}>
        <Text element="div" size="small" color="faded">
          {group.occurrences.map((occurrence) => occurrence.title).join(", ")}
        </Text>
      </div>
      {allowFinishGroup ? (
        <footer className={styles.footer}>
          <OutlinedButton
            disabled={isFinishingGroup}
            onClick={() => finishGroup()}
            width="fill-container"
          >
            <Trans>Markera som klar</Trans>
          </OutlinedButton>
        </footer>
      ) : null}
    </article>
  );
};
